import { inject, InjectionToken, NgZone } from '@angular/core';
import { ChannelService, ChatClientService, ClientEvent, StreamI18nService } from 'stream-chat-angular';
import { Observable } from 'rxjs';
import { Attachment, Channel, ChannelFilters, ChannelOptions, ChannelSort, StreamChat } from 'stream-chat';
import { runInZone } from '@core';

export interface SendMessageOptions {
  readonly text?: string;
  readonly attachments?: Attachment[];
  readonly quotedMessageId?: string;
  readonly mentionedUserIds?: string[];
}

export interface ChannelInitOptions {
  readonly filters: ChannelFilters;
  readonly sort?: ChannelSort;
  readonly options?: ChannelOptions;
  readonly jumpToFirstUnreadChannel?: boolean;
}

export interface StreamChannelProvider {
  readonly activeChannel$: Observable<Channel | undefined>;
  readonly activeChannelMessages$: Observable<unknown[] | undefined>;
  readonly channels$: Observable<Channel[] | undefined>;
  readonly hasMoreChannels$: Observable<boolean>;

  getActiveChannel(): Channel | undefined;
  setAsActiveChannel(channel: Channel): Promise<void>;
  reset(): void;
  deselectActiveChannel(): void;
  initChannels(options: ChannelInitOptions): Promise<Channel[]>;
  loadMoreChannels(): Promise<void>;
  markActiveChannelUnread(messageId: string): Promise<void>;

  sendReaction(messageId: string, reactionType: string): Promise<void>;
  deleteReaction(messageId: string, reactionType: string): Promise<void>;
  sendMessage(options: SendMessageOptions): Promise<void>;
  sendImage(file: File): Promise<{ file: string }>;
}

export interface StreamChatClientProvider {
  readonly chatClient: StreamChat;
  readonly events$: Observable<ClientEvent>;

  getCurrentUserId(): string | undefined;
  init(apiKey: string, userId: string, userToken: string): Promise<void>;
  disconnectUser(): Promise<void>;
  queryChannels(filters: ChannelFilters, sort?: ChannelSort, options?: ChannelOptions): Promise<Channel[]>;
  setTranslation(language: string, translation: Record<string, string>): void;

  deleteMessage(messageId: string): Promise<void>;
  updateMessage(messageId: string, text: string): Promise<void>;
}

export const STREAM_CHANNEL_TOKEN: InjectionToken<StreamChannelProvider> = new InjectionToken<StreamChannelProvider>(
  'STREAM_CHANNEL_TOKEN',
  {
    factory: (): StreamChannelProvider => {
      const channelService: ChannelService = inject(ChannelService);
      const ngZone: NgZone = inject(NgZone);

      return {
        activeChannel$: channelService.activeChannel$.pipe(runInZone(ngZone)),
        activeChannelMessages$: channelService.activeChannelMessages$.pipe(runInZone(ngZone)),
        channels$: channelService.channels$.pipe(runInZone(ngZone)),
        hasMoreChannels$: channelService.hasMoreChannels$.pipe(runInZone(ngZone)),

        getActiveChannel(): Channel | undefined {
          return channelService.activeChannel;
        },

        async setAsActiveChannel(channel: Channel): Promise<void> {
          await channel.watch();
          channelService.setAsActiveChannel(channel);
        },

        reset(): void {
          channelService.reset();
        },

        deselectActiveChannel(): void {
          channelService.deselectActiveChannel();
        },

        async initChannels(options: ChannelInitOptions): Promise<Channel[]> {
          return channelService.init(options.filters, options.sort, options.options, options.jumpToFirstUnreadChannel);
        },

        async loadMoreChannels(): Promise<void> {
          await channelService.loadMoreChannels();
        },

        async markActiveChannelUnread(messageId: string): Promise<void> {
          await channelService.activeChannel?.markUnread({ message_id: messageId });
        },

        async sendReaction(messageId: string, reactionType: string): Promise<void> {
          const channel: Channel | undefined = channelService.activeChannel;
          if (channel) {
            await channel.sendReaction(messageId, { type: reactionType }, { enforce_unique: true });
          }
        },

        async deleteReaction(messageId: string, reactionType: string): Promise<void> {
          const channel: Channel | undefined = channelService.activeChannel;
          if (channel) {
            await channel.deleteReaction(messageId, reactionType);
          }
        },

        async sendMessage(options: SendMessageOptions): Promise<void> {
          const channel: Channel | undefined = channelService.activeChannel;
          if (channel) {
            await channel.sendMessage({
              text: options.text,
              attachments: options.attachments,
              quoted_message_id: options.quotedMessageId,
              mentioned_users: options.mentionedUserIds
            });
          }
        },

        async sendImage(file: File): Promise<{ file: string }> {
          const channel: Channel | undefined = channelService.activeChannel;
          if (!channel) {
            throw new Error('No active channel');
          }
          return channel.sendImage(file);
        }
      };
    }
  }
);

export const STREAM_CHAT_CLIENT_TOKEN: InjectionToken<StreamChatClientProvider> =
  new InjectionToken<StreamChatClientProvider>('STREAM_CHAT_CLIENT_TOKEN', {
    factory: (): StreamChatClientProvider => {
      const chatClientService: ChatClientService = inject(ChatClientService);
      const streamI18nService: StreamI18nService = inject(StreamI18nService);
      const ngZone: NgZone = inject(NgZone);

      return {
        chatClient: chatClientService.chatClient,
        events$: chatClientService.events$.pipe(runInZone(ngZone)),

        getCurrentUserId(): string | undefined {
          return chatClientService.chatClient?.user?.id;
        },

        async init(apiKey: string, userId: string, userToken: string): Promise<void> {
          await chatClientService.init(apiKey, userId, userToken);
        },

        async disconnectUser(): Promise<void> {
          await chatClientService.disconnectUser();
        },

        async queryChannels(filters: ChannelFilters, sort?: ChannelSort, options?: ChannelOptions): Promise<Channel[]> {
          return chatClientService.chatClient.queryChannels(filters, sort, options);
        },

        setTranslation(language: string, translation: Record<string, string>): void {
          streamI18nService.setTranslation(language, translation);
        },

        async deleteMessage(messageId: string): Promise<void> {
          await chatClientService.chatClient.deleteMessage(messageId);
        },

        async updateMessage(messageId: string, text: string): Promise<void> {
          await chatClientService.chatClient.updateMessage({ id: messageId, text });
        }
      };
    }
  });
